import type {
  CalendarMeetingItem,
  CalendarMeetingsResponse,
  ManualMeetingResponse,
} from '@wundo/shared/calendar.ts';
import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';

export type { CalendarMeetingItem, ManualMeetingResponse };

/**
 * The signed-in user's meetings in a date range.
 *
 * The endpoint is addressed by user id rather than by "me" because the same
 * route serves a superadmin looking at someone else's calendar; the policy
 * decides who may read it, not the path.
 */
export async function fetchMeetings(
  userId: string,
  from: Date,
  to: Date,
): Promise<CalendarMeetingItem[]> {
  const query = new URLSearchParams({
    from: from.toISOString(),
    to: to.toISOString(),
  });
  const { items } = await apiFetchJson<CalendarMeetingsResponse>(
    `/users/${userId}/meetings?${query.toString()}`,
  );

  return items;
}

/**
 * Whether this user has any meeting at all, at any point in time.
 *
 * The agenda asks this to tell an agenda that has never held anything from a
 * week that happens to be quiet — a question a week's own fetch cannot answer.
 * One row settles it, so the range is left open at the far end and the page is
 * a single meeting.
 */
export async function fetchHasAnyMeeting(userId: string): Promise<boolean> {
  const query = new URLSearchParams({
    from: new Date(0).toISOString(),
    limit: '1',
  });
  const { items } = await apiFetchJson<CalendarMeetingsResponse>(
    `/users/${userId}/meetings?${query.toString()}`,
  );

  return items.length > 0;
}

/** Who a manual meeting is with, as the user typed them: a name, no address. */
export interface ManualMeetingAttendee {
  name: string;
}

/**
 * Write a meeting down by hand, for a user whose calendar Wundo cannot see.
 *
 * A title, a moment, a gesprekstype and optionally who it is with; the meeting
 * lasts an hour because nobody was asked. The type is sent because it cannot be
 * guessed — there is no room to detect a kind from — so the form asks for it
 * and the server insists on it. Returns the created meeting, so a caller adding
 * several in a row knows what it just made.
 */
export async function createManualMeeting(
  userId: string,
  meeting: {
    title: string;
    starts_at: Date;
    template_id: string;
    attendees?: ManualMeetingAttendee[];
  },
): Promise<ManualMeetingResponse> {
  return apiFetchJson<ManualMeetingResponse>(`/users/${userId}/meetings`, {
    method: 'POST',
    body: JSON.stringify({
      title: meeting.title,
      starts_at: meeting.starts_at.toISOString(),
      template_id: meeting.template_id,
      attendees: meeting.attendees,
    }),
  });
}

/**
 * Change what it says, when it is, which gesprekstype it follows, or who it is
 * with. A field left out is left alone, so renaming a meeting never moves it.
 *
 * `attendees` is the whole list when it is sent — the chips the dialog now
 * holds — so an empty array is the user having taken the last name off.
 */
export async function updateManualMeeting(
  userId: string,
  meetingId: string,
  change: {
    title?: string;
    starts_at?: Date;
    template_id?: string;
    attendees?: ManualMeetingAttendee[];
  },
): Promise<ManualMeetingResponse> {
  return apiFetchJson<ManualMeetingResponse>(`/users/${userId}/meetings/${meetingId}`, {
    method: 'PUT',
    body: JSON.stringify({
      title: change.title,
      starts_at: change.starts_at?.toISOString(),
      template_id: change.template_id,
      attendees: change.attendees,
    }),
  });
}

/** Take it back off the agenda, with everything that hung off it. */
export async function deleteManualMeeting(
  userId: string,
  meetingId: string,
): Promise<void> {
  await apiFetch(`/users/${userId}/meetings/${meetingId}`, {
    method: 'DELETE',
  });
}
